import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react';
import RegisterPopup from './RegisterPopup';
import { motion, useScroll, useTransform } from 'framer-motion';
import SimpleButton from '@components/Buttons/SimpleButton';
import StructuredTextParser from '../../app/landing_pages/karma/Components/StructuredTextParser';
import { render } from 'datocms-structured-text-to-html-string';
import { VdoCipherPlayer } from '@components/VdoCipherPlayer/vdo-cipher-player.tsx';
import { mixPanel } from '@utils/Mixpanel';
import { useRegisterPopupVisibilityContext } from '@landing_pages/margazhi/context/registerPopupVisibilityContext';

const MotionBox = motion(Box);

function TopBannerSectionWrapper({ sectionContent, pageData }) {
  const { isOpen, onOpen, onClose, isMounted } =
    useRegisterPopupVisibilityContext();
  const fallbackImage = useBreakpointValue({
    base: sectionContent?.[0]?.mobileImage,
    md: sectionContent?.[0]?.desktopImage,
  });
  const ref = useRef(null);

  // Scroll tracking
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // First block transitions (fades out)
  const opacityFirst = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const yFirst = useTransform(scrollYProgress, [0, 0.4], ['0%', '-20%']);

  // Second block transitions (fades in)
  const opacitySecond = useTransform(scrollYProgress, [0.4, 0.8], [0, 1]);
  const ySecond = useTransform(scrollYProgress, [0.4, 0.8], ['20%', '0%']);

  const mixPanelTrigger = () => {
    const Mixpanel = mixPanel(window?.location.pathname);
    Mixpanel.track('margazhi_register_now_cta_click', {
      cta: 'top cta',
      element: 'REGISTER NOW',
    });
  };

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const target = document.getElementById('margazhiTopBanner');
    if (!target) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        root: null, // observe relative to viewport
        rootMargin: '0px', // no margin
        threshold: 0.1, // 10% visible triggers
      }
    );
    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  const commonButtonStyles = {
    background: '#CF4520 !important',
    h: { base: '54px' },
    textAlign: 'center',
    mb: '0',
    mt: '0',
    mx: { base: '0', md: '0' },
    _hover: {
      background: '#AB3312 !important',
    },
    style: {
      ...sectionContent?.[0]?.button?.[0],
      buttonText: (
        <Box
          justifyContent={'center'}
          display="flex"
          alignItems="center"
          gap="8px"
          height={'34px'}
          className="bottomButton"
        >
          <Box
            className="allSmallCase"
            lineHeight={'20px'}
            paddingBottom={'6px'}
          >
            {sectionContent?.[0]?.button?.[0]?.buttonText}
          </Box>
          <Box>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="12"
              viewBox="0 0 15 12"
              fill="none"
            >
              <path
                d="M8.21875 0.75L13.4688 5.99999L8.21875 11.25M0.53125 5.99999L13 6"
                stroke="white"
                stroke-width="2"
              />
            </svg>
          </Box>
        </Box>
      ),
    },
    padding: {
      base: '10px 32px',
      lg: '10px',
    },
    lineHeight: {
      base: '34px',
    },
    color: '#fff',
    fontSize: '22px',
    fontFamily: 'FedraSansStd-medium',
    letterSpacing: '2.88px',
    zIndex: 100,
    onClick: () => {
      onOpen();
      mixPanelTrigger();
    },
    textTransform: 'uppercase',
    cursor: 'pointer',
    useBlankStringURLOnLink: true,
  };

  let desktopVideos = sectionContent?.[0]?.desktopVideo?.match(/[a-f0-9]{32}/g);
  let mobileVideos = sectionContent?.[0]?.mobileVideo?.match(/[a-f0-9]{32}/g);

  let bgVideo = useBreakpointValue({
    base: mobileVideos?.[0],
    xl: desktopVideos?.[0],
    '2xl': desktopVideos?.[1],
  });
  let bgVideoAspectRatio = useBreakpointValue({ base: '9/16', xl: '16/9' });
  let videoContainerId = `TopBanner${bgVideo}`;
  return (
    <>
      <Box
        id={'margazhiTopBanner'}
        ref={ref}
        position="relative"
        height="250vh"
      >
        <Box
          position="sticky"
          top="0"
          // height="100vh"
          // backgroundImage={"url('/img/marghazi/top_section_bg.png')"}
          backgroundSize="cover"
          backgroundPosition="center"
          color="white"
          zIndex={2}
        >
          <Box
            position="absolute"
            w="100%"
            zIndex={1}
            h={{ base: '50%', md: '54%' }}
            bottom="0"
            left="0"
            className="NEW_CLASS"
          >
            <Box
              bg="linear-gradient(180deg, rgba(6, 20, 42, 0.00) 2.96%, rgba(6, 20, 42, 0.80) 68.95%)"
              h="100%"
              display={{ base: 'none', md: 'block' }}
            />

            <Box display={{ base: 'block', md: 'none' }} h="100%">
              <Box
                bg="linear-gradient(0deg, #06142A 0%, rgba(6, 20, 42, 0.00) 100%)"
                h="20%"
                marginBottom={'-0.5px'}
              />
              <Box bg="#06142A" h="calc(100% - 20%)" />
            </Box>
          </Box>
          <Box width={'100%'} maxH={'100vh'} overflow={'hidden'} zIndex={1}>
            <VdoCipherPlayer
              id={videoContainerId}
              videoId={bgVideo}
              controlAutoPlay={true}
              startVideoInUseEffect={isVisible}
              startVideo={true}
              muteStatus={true}
              hideFullScreen={true}
              hideSoundIcon={true}
              hideDragProgBar={true}
              hidePlayPauseIcon={true}
              aspectRatio={bgVideoAspectRatio}
              loopVideo={true}
              showVideoInFullScreen
              showFallbackImage
              fallbackImageSrc={fallbackImage?.url}
              fallbackImageAlt={fallbackImage?.alt}
              fallbackImageStyle={{
                objectFit: 'cover',
                width: '100vw',
                height: '100vh',
              }}
              fallbackImageStyleProps={{ width: '100vw', height: '100vh' }}
              videoQualityId={1}
              parentId="#margazhiTopBanner"
            />
          </Box>
          <MotionBox
            style={{ opacity: opacityFirst, y: yFirst }}
            position="absolute"
            bottom={{ base: '74px', md: '84px' }}
            left="0"
            w={'100%'}
            zIndex={3}
          >
            <Box
              w={'100%'}
              display={'flex'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '24px', md: '0px' }}
              px={"16px"}
              maxW={{ base: '100%', md: '1202px' }}
              mx={'auto'}
            >
              <Flex
                flexDirection={'column'}
                maxW={{ base: '246px', md: '400px', lg: '481px' }}
              >
                <Text
                  fontSize={{ base: '56px', md: '96px', lg: '96px' }}
                  lineHeight={{ base: '56px', md: '96px', lg: '96px' }}
                  fontWeight={'400'}
                  letterSpacing={{
                    base: '-1.12px',
                    md: '-1.92px',
                    lg: '-1.92px',
                  }}
                  fontFamily="FedraSerifDisplayRegular"
                  color="white"
                >
                  {sectionContent?.[0]?.title}
                </Text>
                <StructuredTextParser
                  fontSize={{ base: '16px', md: '24px', lg: '24px' }}
                  lineHeight={{ base: '22.4px', md: '33.6px', lg: '33.6px' }}
                  fontWeight={'400'}
                  letterSpacing={{ base: '0.32px', md: '0.48px', lg: '0.48px' }}
                  fontFamily="FedraSansStd-book"
                  str={render(sectionContent?.[0]?.titleSubtext)}
                  pt={{ base: '8px', md: '0px' }}
                  mt="0px"
                />
              </Flex>
              <Flex
                flexDirection={'column'}
                justifyContent={{ base: 'center', md: 'space-between' }}
                alignItems={'flex-start'}
              >
                <VStack
                  align="flex-start"
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                  spacing={2}
                  borderLeft={'2px solid #CF4520'}
                  ml={{ base: '0', md: '32px' }}
                  mt={{ base: '0px', md: '10px' }}
                  pl={4}
                >
                  <HStack spacing={4} alignItems={'flex-start'}>
                    <Image
                      src={sectionContent?.[0]?.icon?.[0]?.url}
                      alt={sectionContent?.[0]?.icon?.[0]?.alt || 'image'}
                      w={{ base: '24px', md: '32px' }}
                      h={{ base: '24px', md: '32px' }}
                      objectFit="contain"
                      loading="lazy"
                    />
                    <StructuredTextParser
                      fontSize={{ base: '16px', md: '24px', lg: '24px' }}
                      lineHeight={{
                        base: '22.4px',
                        md: '33.6px',
                        lg: '33.6px',
                      }}
                      fontWeight={'450'}
                      letterSpacing={{
                        base: '0.32px',
                        md: '0.48px',
                        lg: '0.48px',
                      }}
                      fontFamily="FedraSansStd-book"
                      str={render(sectionContent?.[0]?.date)}
                      mt="0px"
                    />
                  </HStack>

                  <HStack spacing={4}>
                    <Image
                      src={sectionContent?.[0]?.icon?.[1]?.url}
                      alt={sectionContent?.[0]?.icon?.[1]?.alt || 'image'}
                      w={{ base: '24px', md: '32px' }}
                      h={{ base: '24px', md: '32px' }}
                      objectFit="contain"
                      loading="lazy"
                    />
                    <StructuredTextParser
                      fontSize={{ base: '16px', md: '24px', lg: '24px' }}
                      lineHeight={{
                        base: '22.4px',
                        md: '33.6px',
                        lg: '33.6px',
                      }}
                      fontWeight={'450'}
                      letterSpacing={{
                        base: '0.32px',
                        md: '0.48px',
                        lg: '0.48px',
                      }}
                      fontFamily="FedraSansStd-book"
                      str={render(sectionContent?.[0]?.location)}
                      mt="0px"
                    />
                  </HStack>
                </VStack>
                <Box
                  pt={'16px'}
                  display={{ base: 'none', md: 'block' }}
                  id="register-button"
                  className="register-button"
                >
                  <SimpleButton
                    {...commonButtonStyles}
                    w="max-content"
                    minW={{ base: '216px', lg: '242px' }}
                    borderRadius="4px !important"
                    ml={{ base: '0', md: '32px' }}
                  />
                </Box>
              </Flex>
            </Box>
          </MotionBox>
          <MotionBox
            style={{ opacity: opacitySecond, y: ySecond }}
            position="absolute"
            bottom={{ base: '68px', md: '77px' }}
            left="0"
            w={'100%'}
            zIndex={2}
            className={'second-message'}
          >
            <Box
              w={'100%'}
              maxW={'1202px'}
              px={'16px'}
              mx={'auto'}
              pb={{ base: '28px', lg: '0px' }}
            >
              <StructuredTextParser
                maxW={{ base: '100%', md: '1200px' }}
                str={render(sectionContent?.[0]?.closedMessage)}
                sectionContent={sectionContent}
                fontSize={{ base: '24px', md: '48px' }}
                letterSpacing={'-2px'}
                lineHeight={{ base: '33.6px', md: '57.6px' }}
                fontFamily="FedraSansStd-book"
                color={'white'}
                fontStyle={'italic'}
                mt="0px"
                py={{ base: 2, md: 2 }}
                borderLeft={'2px solid #CF4520'}
                pl={{ base: '24px', md: '48px' }}
                mx={'auto'}
              />
            </Box>
            <Box
              position={'absolute'}
              bottom={{ base: '0px', md: '-27px', xl: '-50px', '2xl': '-27px' }}
              right={'0px'}
              display={{ base: 'none', md: 'block' }}
              id="register-button"
              className="register-button"
            >
              <SimpleButton
                {...commonButtonStyles}
                w="max-content"
                minW={{ base: '216px', lg: '242px' }}
                borderRadius="32px 0 0 32px !important"
              />
            </Box>
          </MotionBox>
        </Box>
        <style jsx global>
          {`
            .bottomButton button {
              width: 100%;
            }
            .second-message mark {
              background: transparent;
              color: #ff744e;
            }
            .allSmallCase {
              font-variant: all-small-caps;
            }
          `}
        </style>
      </Box>

      <RegisterPopup
        sectionContent={sectionContent}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        pageData={pageData}
        isMounted={isMounted}
      />
    </>
  );
}

export default TopBannerSectionWrapper;
